// VISFile.js
//
// Reader for SportsStreamLab / VolleyStation ".vis" volleyball files.
//
// A .vis file is NOT a bespoke format: it is the raw output of the .NET
// `BinaryFormatter` serializer (Microsoft's MS-NRBF "Remoting Binary Format").
// The app serialized a graph of C# objects (Class_VisSingle -> Class_vMatch,
// List<Class_RallyItem>, etc.) straight to disk.
//
// There is no portable library for NRBF, so this is a small hand-written reader
// that supports only the record/type codes these files actually emit. It parses
// the byte stream into plain JS objects/arrays/strings (it never instantiates
// real .NET types, so it sidesteps the BinaryFormatter deserialization security
// risk entirely), then a second `resolve` pass replaces back-references with the
// real objects (handling cyclic references), and finally `visToJson` flattens
// the C# `<Name>k__BackingField` member names into clean JSON.
//
// Ported from the reference Python implementation (nrbf.py / export_full_json.py).
// Reference spec: [MS-NRBF].

// ---------------------------------------------------------------------------
// Record type tags (RecordTypeEnumeration)
// ---------------------------------------------------------------------------
const REC = {
  SerializationHeader: 0,
  ClassWithId: 1,
  SystemClassWithMembers: 2,
  ClassWithMembers: 3,
  SystemClassWithMembersAndTypes: 4,
  ClassWithMembersAndTypes: 5,
  BinaryObjectString: 6,
  BinaryArray: 7,
  MemberPrimitiveTyped: 8,
  MemberReference: 9,
  ObjectNull: 10,
  MessageEnd: 11,
  BinaryLibrary: 12,
  ObjectNullMultiple256: 13,
  ObjectNullMultiple: 14,
  ArraySinglePrimitive: 15,
  ArraySingleObject: 16,
  ArraySingleString: 17,
};

// BinaryTypeEnumeration (member static types)
const BT = {
  Primitive: 0,
  String: 1,
  Object: 2,
  SystemClass: 3,
  Class: 4,
  ObjectArray: 5,
  StringArray: 6,
  PrimitiveArray: 7,
};

// PrimitiveTypeEnumeration
const PT = {
  Boolean: 1,
  Byte: 2,
  Char: 3,
  Decimal: 5,
  Double: 6,
  Int16: 7,
  Int32: 8,
  Int64: 9,
  SByte: 10,
  Single: 11,
  TimeSpan: 12,
  DateTime: 13,
  UInt16: 14,
  UInt32: 15,
  UInt64: 16,
  Null: 17,
  String: 18,
};

// Sentinel returned when the top-level MessageEnd record is reached.
const END = Symbol("MessageEnd");

// .NET ticks (100ns) between 0001-01-01 and the Unix epoch (1970-01-01).
const TICKS_EPOCH = 621355968000000000n;

// ---------------------------------------------------------------------------
// Low-level little-endian cursor over the raw bytes.
// ---------------------------------------------------------------------------
class Reader {
  constructor(buffer) {
    this.bytes = new Uint8Array(buffer);
    this.view = new DataView(
      this.bytes.buffer,
      this.bytes.byteOffset,
      this.bytes.byteLength
    );
    this.pos = 0;
    this._utf8 = new TextDecoder("utf-8");
  }

  eof() {
    return this.pos >= this.bytes.length;
  }

  byte() {
    return this.bytes[this.pos++];
  }

  int8() {
    const v = this.view.getInt8(this.pos);
    this.pos += 1;
    return v;
  }

  uint8() {
    const v = this.view.getUint8(this.pos);
    this.pos += 1;
    return v;
  }

  int16() {
    const v = this.view.getInt16(this.pos, true);
    this.pos += 2;
    return v;
  }

  uint16() {
    const v = this.view.getUint16(this.pos, true);
    this.pos += 2;
    return v;
  }

  int32() {
    const v = this.view.getInt32(this.pos, true);
    this.pos += 4;
    return v;
  }

  uint32() {
    const v = this.view.getUint32(this.pos, true);
    this.pos += 4;
    return v;
  }

  int64() {
    const v = this.view.getBigInt64(this.pos, true);
    this.pos += 8;
    return v;
  }

  uint64() {
    const v = this.view.getBigUint64(this.pos, true);
    this.pos += 8;
    return v;
  }

  single() {
    const v = this.view.getFloat32(this.pos, true);
    this.pos += 4;
    return v;
  }

  double() {
    const v = this.view.getFloat64(this.pos, true);
    this.pos += 8;
    return v;
  }

  // 7-bit-encoded variable-length unsigned int (BinaryReader string-length prefix).
  varint() {
    let result = 0;
    let shift = 0;
    let b;
    do {
      b = this.byte();
      result |= (b & 0x7f) << shift;
      shift += 7;
    } while (b & 0x80);
    return result >>> 0;
  }

  // Length-prefixed UTF-8 string (same scheme as BinaryReader.Write(string)).
  string() {
    const len = this.varint();
    const slice = this.bytes.subarray(this.pos, this.pos + len);
    this.pos += len;
    return this._utf8.decode(slice);
  }

  // A single .NET Char: one Unicode code point, UTF-8 encoded (1-4 bytes).
  char() {
    const first = this.bytes[this.pos];
    let len = 1;
    if (first >= 0xf0) len = 4;
    else if (first >= 0xe0) len = 3;
    else if (first >= 0xc0) len = 2;
    const slice = this.bytes.subarray(this.pos, this.pos + len);
    this.pos += len;
    return this._utf8.decode(slice);
  }
}

// ---------------------------------------------------------------------------
// Helpers for .NET temporal primitives.
// ---------------------------------------------------------------------------
function decodeDateTime(rawU64) {
  // Top 2 bits encode DateTimeKind; the low 62 bits are ticks since 0001-01-01.
  const ticks = rawU64 & 0x3fffffffffffffffn;
  const ms = Number((ticks - TICKS_EPOCH) / 10000n);
  if (!Number.isFinite(ms)) return null;
  const d = new Date(ms);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

// ---------------------------------------------------------------------------
// NRBF parser: produces a generic tree of plain objects/arrays/strings, plus a
// registry of every id-bearing object so references can be resolved afterwards.
// ---------------------------------------------------------------------------
class NRBFParser {
  constructor(buffer) {
    this.r = new Reader(buffer);
    this.registry = new Map(); // objectId -> object/array/string
    this.classMeta = new Map(); // metadataId -> { name, memberNames, binaryTypes, additional }
    this.libraries = new Map(); // libraryId -> name
    this.rootId = null;
  }

  load() {
    this.readHeader();
    // Read top-level records until MessageEnd. Nested records are consumed
    // recursively, so most files contain just the root object then MessageEnd.
    let guard = 0;
    while (!this.r.eof()) {
      const rec = this.readRecord();
      if (rec === END) break;
      if (++guard > 5_000_000) {
        throw new Error("VIS parse aborted: too many top-level records");
      }
    }
    const root = this.registry.get(this.rootId);
    resolveGraph(root, this.registry);
    return root;
  }

  readHeader() {
    const tag = this.r.byte();
    if (tag !== REC.SerializationHeader) {
      throw new Error(
        `Not an NRBF/.vis stream: expected header tag 0, got ${tag}`
      );
    }
    this.rootId = this.r.int32();
    this.r.int32(); // headerId
    this.r.int32(); // majorVersion
    this.r.int32(); // minorVersion
  }

  // Read a single record (skipping any BinaryLibrary declarations). Returns the
  // parsed value, or END for MessageEnd.
  readRecord() {
    let tag = this.r.byte();
    while (tag === REC.BinaryLibrary) {
      this.readLibrary();
      tag = this.r.byte();
    }
    return this.parseRecordByTag(tag);
  }

  readLibrary() {
    const libraryId = this.r.int32();
    const name = this.r.string();
    this.libraries.set(libraryId, name);
  }

  parseRecordByTag(tag) {
    switch (tag) {
      case REC.ClassWithId:
        return this.parseClassWithId();
      case REC.SystemClassWithMembersAndTypes:
        return this.parseClassWithMembersAndTypes(true);
      case REC.ClassWithMembersAndTypes:
        return this.parseClassWithMembersAndTypes(false);
      case REC.BinaryObjectString:
        return this.parseBinaryObjectString();
      case REC.BinaryArray:
        return this.parseBinaryArray();
      case REC.MemberPrimitiveTyped: {
        const code = this.r.byte();
        return this.readPrimitive(code);
      }
      case REC.MemberReference:
        return { $ref: this.r.int32() };
      case REC.ObjectNull:
        return null;
      case REC.MessageEnd:
        return END;
      case REC.ArraySinglePrimitive:
        return this.parseArraySinglePrimitive();
      case REC.ArraySingleObject:
        return this.parseArraySingleObject();
      case REC.ArraySingleString:
        return this.parseArraySingleString();
      default:
        throw new Error(
          `Unknown NRBF record tag ${tag} at offset ${this.r.pos - 1}`
        );
    }
  }

  // ClassInfo: objectId, name, memberCount, memberNames[].
  readClassInfo() {
    const objectId = this.r.int32();
    const name = this.r.string();
    const memberCount = this.r.int32();
    const memberNames = [];
    for (let i = 0; i < memberCount; i++) {
      memberNames.push(this.r.string());
    }
    return { objectId, name, memberNames };
  }

  // MemberTypeInfo: a BinaryType per member, then the additional info each needs.
  readMemberTypeInfo(memberCount) {
    const binaryTypes = [];
    for (let i = 0; i < memberCount; i++) {
      binaryTypes.push(this.r.byte());
    }
    const additional = [];
    for (let i = 0; i < memberCount; i++) {
      const bt = binaryTypes[i];
      if (bt === BT.Primitive || bt === BT.PrimitiveArray) {
        additional.push({ primitive: this.r.byte() });
      } else if (bt === BT.SystemClass) {
        additional.push({ className: this.r.string() });
      } else if (bt === BT.Class) {
        additional.push({
          className: this.r.string(),
          libraryId: this.r.int32(),
        });
      } else {
        additional.push(null);
      }
    }
    return { binaryTypes, additional };
  }

  parseClassWithMembersAndTypes(isSystem) {
    const info = this.readClassInfo();
    const { binaryTypes, additional } = this.readMemberTypeInfo(
      info.memberNames.length
    );
    if (!isSystem) {
      this.r.int32(); // libraryId
    }
    this.classMeta.set(info.objectId, {
      name: info.name,
      memberNames: info.memberNames,
      binaryTypes,
      additional,
    });
    return this.readClassValues(info.objectId, info.name, {
      memberNames: info.memberNames,
      binaryTypes,
      additional,
    });
  }

  parseClassWithId() {
    const objectId = this.r.int32();
    const metadataId = this.r.int32();
    const meta = this.classMeta.get(metadataId);
    if (!meta) {
      throw new Error(`ClassWithId references unknown metadata id ${metadataId}`);
    }
    return this.readClassValues(objectId, meta.name, meta);
  }

  readClassValues(objectId, name, meta) {
    const obj = {};
    Object.defineProperty(obj, "$type", { value: name, enumerable: false });
    Object.defineProperty(obj, "$id", { value: objectId, enumerable: false });
    // Register before reading members so self-referential graphs can resolve.
    this.registry.set(objectId, obj);
    for (let i = 0; i < meta.memberNames.length; i++) {
      obj[meta.memberNames[i]] = this.readValue(
        meta.binaryTypes[i],
        meta.additional[i]
      );
    }
    return obj;
  }

  // Reads one member value. Only BinaryType.Primitive is inlined; every other
  // (reference) type is encoded as its own record in the stream.
  readValue(binaryType, addl) {
    if (binaryType === BT.Primitive) {
      return this.readPrimitive(addl.primitive);
    }
    return this.readRecord();
  }

  readPrimitive(code) {
    switch (code) {
      case PT.Boolean:
        return this.r.byte() !== 0;
      case PT.Byte:
        return this.r.byte();
      case PT.Char:
        return this.r.char();
      case PT.Decimal:
        return this.r.string(); // .NET Decimal serialized as its string form.
      case PT.Double:
        return this.r.double();
      case PT.Int16:
        return this.r.int16();
      case PT.Int32:
        return this.r.int32();
      case PT.Int64:
        return Number(this.r.int64());
      case PT.SByte:
        return this.r.int8();
      case PT.Single:
        return this.r.single();
      case PT.TimeSpan:
        // Raw 100ns ticks (a time-of-day / duration); callers format as needed.
        return Number(this.r.int64());
      case PT.DateTime:
        return decodeDateTime(this.r.uint64());
      case PT.UInt16:
        return this.r.uint16();
      case PT.UInt32:
        return this.r.uint32();
      case PT.UInt64:
        return Number(this.r.uint64());
      case PT.Null:
        return null;
      case PT.String:
        return this.r.string();
      default:
        throw new Error(`Unknown NRBF primitive type ${code}`);
    }
  }

  parseBinaryObjectString() {
    const objectId = this.r.int32();
    const value = this.r.string();
    this.registry.set(objectId, value);
    return value;
  }

  parseArraySinglePrimitive() {
    const objectId = this.r.int32();
    const length = this.r.int32();
    const code = this.r.byte();
    const arr = [];
    for (let i = 0; i < length; i++) {
      arr.push(this.readPrimitive(code));
    }
    this.registry.set(objectId, arr);
    return arr;
  }

  parseArraySingleObject() {
    const objectId = this.r.int32();
    const length = this.r.int32();
    const arr = this.readObjectElements(length);
    this.registry.set(objectId, arr);
    return arr;
  }

  parseArraySingleString() {
    const objectId = this.r.int32();
    const length = this.r.int32();
    const arr = this.readObjectElements(length);
    this.registry.set(objectId, arr);
    return arr;
  }

  parseBinaryArray() {
    const objectId = this.r.int32();
    const arrayType = this.r.byte(); // BinaryArrayTypeEnumeration
    const rank = this.r.int32();
    const lengths = [];
    for (let i = 0; i < rank; i++) {
      lengths.push(this.r.int32());
    }
    // Offset array types (3,4,5) carry lower bounds.
    if (arrayType === 3 || arrayType === 4 || arrayType === 5) {
      for (let i = 0; i < rank; i++) {
        this.r.int32();
      }
    }
    const { binaryTypes, additional } = this.readMemberTypeInfo(1);
    const total = lengths.reduce((a, b) => a * b, 1);
    let arr;
    if (binaryTypes[0] === BT.Primitive) {
      arr = [];
      for (let i = 0; i < total; i++) {
        arr.push(this.readPrimitive(additional[0].primitive));
      }
    } else {
      arr = this.readObjectElements(total);
    }
    this.registry.set(objectId, arr);
    return arr;
  }

  // Reads `count` reference-typed array elements, expanding the run-length
  // null records (ObjectNull / ObjectNullMultiple) into individual nulls.
  readObjectElements(count) {
    const out = [];
    while (out.length < count) {
      let tag = this.r.byte();
      while (tag === REC.BinaryLibrary) {
        this.readLibrary();
        tag = this.r.byte();
      }
      if (tag === REC.ObjectNull) {
        out.push(null);
      } else if (tag === REC.ObjectNullMultiple256) {
        const n = this.r.byte();
        for (let i = 0; i < n; i++) out.push(null);
      } else if (tag === REC.ObjectNullMultiple) {
        const n = this.r.int32();
        for (let i = 0; i < n; i++) out.push(null);
      } else {
        out.push(this.parseRecordByTag(tag));
      }
    }
    return out;
  }
}

// ---------------------------------------------------------------------------
// Second pass: replace { $ref: id } placeholders with the real registry object,
// in place, tolerating cyclic references via a visited set.
// ---------------------------------------------------------------------------
function resolveGraph(root, registry) {
  const seen = new Set();

  const resolveValue = (v) => {
    if (v && typeof v === "object") {
      if (v.$ref !== undefined && Object.keys(v).length === 1) {
        return walk(registry.get(v.$ref));
      }
      return walk(v);
    }
    return v;
  };

  const walk = (node) => {
    if (!node || typeof node !== "object") return node;
    if (seen.has(node)) return node;
    seen.add(node);
    if (Array.isArray(node)) {
      for (let i = 0; i < node.length; i++) {
        node[i] = resolveValue(node[i]);
      }
    } else {
      for (const k of Object.keys(node)) {
        node[k] = resolveValue(node[k]);
      }
    }
    return node;
  };

  return walk(root);
}

// ---------------------------------------------------------------------------
// Map the raw parsed graph into a clean, flattened JSON schema (competition,
// match, referees, team_a/team_b with roster + officials, sets, rally_events).
//
// This is a faithful port of the reference export_full_json.py. The deeply
// nested C# backing-field names (`<X>k__BackingField`,
// `ClassName+<X>k__BackingField`) are translated into clean snake_case keys, and
// enums collapse to their integer value. All field lookups are centralised here
// so future VIS schema drift (new app version / extra fields) is easy to patch.
// ---------------------------------------------------------------------------

// Safe member read: returns null for a missing key or a null container.
function g(d, key, def = null) {
  if (!d || typeof d !== "object") return def;
  const v = d[key];
  return v === undefined ? def : v;
}

// Enums come through as a nested object { value__: n }; unwrap to the integer.
function enumVal(d) {
  if (d && typeof d === "object" && "value__" in d) return d.value__;
  return d === undefined ? null : d;
}

// A .NET TimeSpan is stored as raw 100ns ticks; render as HH:MM:SS.
function ticksToHms(t) {
  if (t === null || t === undefined) return null;
  const secs = t / 1e7;
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

function teamBasic(team) {
  // Fields inherited from the Class_bTeam base are prefixed `Class_bTeam+...`.
  const b = {};
  for (const k of Object.keys(team)) {
    if (k.startsWith("Class_bTeam")) {
      b[k.split("+").pop()] = team[k];
    }
  }
  return {
    team_code: enumVal(g(b, "<TeamCode>k__BackingField")),
    short_name: g(b, "<Name>k__BackingField"),
    full_name: g(b, "<NameFull>k__BackingField"),
    country_code: g(b, "<CountryCode>k__BackingField"),
    rank: g(b, "<Rank>k__BackingField"),
    shirt_colors: g(team, "<ShirtColors>k__BackingField"),
    current_shirt_color: g(team, "<CurShirtColor>k__BackingField"),
  };
}

function playerDict(pl) {
  if (pl === null || pl === undefined) return null;
  const roles = [];
  if (g(pl, "<IsCaptain>k__BackingField")) roles.push("Captain");
  if (g(pl, "<IsLibero>k__BackingField")) roles.push("Libero");
  if (g(pl, "<IsSetter>k__BackingField")) roles.push("Setter");
  return {
    number: g(pl, "<Num>k__BackingField"),
    name:
      g(pl, "Class_bPlayer+<NameShirt>k__BackingField") ||
      g(pl, "Class_bPerson+<Name>k__BackingField"),
    full_name: g(pl, "Class_bPerson+<Name>k__BackingField"),
    roles,
    is_captain: g(pl, "<IsCaptain>k__BackingField"),
    is_libero: g(pl, "<IsLibero>k__BackingField"),
    is_setter: g(pl, "<IsSetter>k__BackingField"),
    is_out: g(pl, "<IsOut>k__BackingField"),
    is_show: g(pl, "<IsShow>k__BackingField"),
    position: enumVal(g(pl, "<Position>k__BackingField")),
    match_position: enumVal(g(pl, "<MatchPosition>k__BackingField")),
    birthday: g(pl, "Class_bPerson+<Birthday>k__BackingField"),
    gender: enumVal(g(pl, "Class_bPerson+<Gender>k__BackingField")),
    height_cm: g(pl, "Class_bPerson+<Height>k__BackingField"),
    weight_kg: g(pl, "Class_bPerson+<Weight>k__BackingField"),
    country_code: g(pl, "Class_bPerson+<CountryCode>k__BackingField"),
    register_no: g(pl, "<RegisterNo>k__BackingField"),
    club: g(pl, "Class_bPlayer+<Club>k__BackingField"),
    hand_used: enumVal(g(pl, "Class_bPlayer+<HandUsed>k__BackingField")),
    spike_total: g(pl, "<Spike>k__BackingField"),
    block_total: g(pl, "<Block>k__BackingField"),
  };
}

function officialDict(off) {
  if (off === null || off === undefined) return null;
  return {
    title: g(off, "Class_bOfficial+<Title>k__BackingField"),
    name: g(off, "Class_bPerson+<Name>k__BackingField"),
    country_code: g(off, "Class_bPerson+<CountryCode>k__BackingField"),
    license_id: g(off, "<LicenseID>k__BackingField"),
    is_show: g(off, "<IsShow>k__BackingField"),
  };
}

// List<T> is serialized with an over-allocated `_items` (capacity) and `_size`
// (real count). Roster/official lists also hold nulls for unused jersey slots.
function listItems(list) {
  if (!list || !Array.isArray(list._items)) return [];
  return list._items;
}

function buildTeamBlock(tig, team) {
  const players = listItems(g(team, "<PlayerList>k__BackingField"))
    .map(playerDict)
    .filter((pl) => pl !== null);
  const officials = listItems(g(team, "<OfficialList>k__BackingField"))
    .map(officialDict)
    .filter((o) => o !== null);
  const block = teamBasic(team);
  block.team_in_group_name = g(tig, "<Name>k__BackingField");
  block.roster = players;
  block.officials = officials;
  return block;
}

const REFEREE_KEYS = [
  "<R1>k__BackingField",
  "<R2>k__BackingField",
  "<RR>k__BackingField",
  "<RC>k__BackingField",
  "<SC>k__BackingField",
  "<ASC>k__BackingField",
  "<L1>k__BackingField",
  "<L2>k__BackingField",
  "<L3>k__BackingField",
  "<L4>k__BackingField",
  "<LR>k__BackingField",
];

export function visToJson(root, sourceFile) {
  const match = g(root, "<Match>k__BackingField");
  if (!match) {
    throw new Error("VIS file has no <Match> data (unexpected root shape)");
  }

  const place = g(match, "<Place>k__BackingField");
  const group = g(match, "<Group>k__BackingField");
  const phase = g(group, "<Phase>k__BackingField");
  const cup = g(phase, "Cup");

  const atg = g(match, "<ATeamInGroup>k__BackingField");
  const btg = g(match, "<BTeamInGroup>k__BackingField");
  const ateam = g(atg, "<Team>k__BackingField");
  const bteam = g(btg, "<Team>k__BackingField");

  const referees = [];
  for (const key of REFEREE_KEYS) {
    const r = g(match, key);
    if (r) {
      referees.push({
        role: key.replace(/[<>]/g, "").replace("k__BackingField", ""),
        name: g(r, "Class_bPerson+<Name>k__BackingField"),
        country_code: g(r, "Class_bPerson+<CountryCode>k__BackingField"),
        level: enumVal(g(r, "<Level>k__BackingField")),
      });
    }
  }

  const setList = listItems(g(match, "<SetDataList>k__BackingField")).slice(
    0,
    g(g(match, "<SetDataList>k__BackingField"), "_size", 0)
  );
  const setsOut = [];
  for (const sd of setList) {
    if (!sd) continue;
    const sc = g(sd, "<Score>k__BackingField");
    setsOut.push({
      set_num: g(sd, "<SetNum>k__BackingField"),
      set_state: enumVal(g(sd, "<SetState>k__BackingField")),
      score_a: g(sc, "<A>k__BackingField"),
      score_b: g(sc, "<B>k__BackingField"),
      duration_min: g(sd, "<Duration>k__BackingField"),
      a_lineup_sub_pairs: g(sd, "<ALineupSubPairs>k__BackingField"),
      b_lineup_sub_pairs: g(sd, "<BLineupSubPairs>k__BackingField"),
      a_pos_sub_counts: g(sd, "<APosSubCounts>k__BackingField"),
      b_pos_sub_counts: g(sd, "<BPosSubCounts>k__BackingField"),
      a_libero_shows: g(sd, "<ALiberoShows>k__BackingField"),
      b_libero_shows: g(sd, "<BLiberoShows>k__BackingField"),
    });
  }

  const setsWonA = setsOut.filter((s) => s.score_a > s.score_b).length;
  const setsWonB = setsOut.filter((s) => s.score_b > s.score_a).length;

  const rallyList = g(root, "<RallyList>k__BackingField");
  const rallySize = g(rallyList, "_size", 0);
  const rallyItems = listItems(rallyList).slice(0, rallySize);
  const ralliesOut = [];
  for (const it of rallyItems) {
    if (!it) continue;
    const score = g(it, "Class_vAction+<Score>k__BackingField");
    ralliesOut.push({
      order: g(it, "<Order>k__BackingField"),
      set_num: g(it, "Class_vAction+<SetNum>k__BackingField"),
      rally_start_time: g(it, "RallyStartTime"),
      rally_code: g(it, "<CMD>k__BackingField"),
      is_double_mode: g(it, "<IsDoubleMode>k__BackingField"),
      record_team_code: enumVal(g(it, "<RecordTeamCode>k__BackingField")),
      winning_team_code: enumVal(
        g(it, "Class_vAction+<ActionTeamCode>k__BackingField")
      ),
      serve_team_code: enumVal(
        g(it, "Class_vAction+<ServeTeamCode>k__BackingField")
      ),
      score_a: g(score, "<A>k__BackingField"),
      score_b: g(score, "<B>k__BackingField"),
      a_cur_player_pairs: g(
        it,
        "Class_vAction+<ACurPlayerPairs>k__BackingField"
      ),
      b_cur_player_pairs: g(
        it,
        "Class_vAction+<BCurPlayerPairs>k__BackingField"
      ),
      a_serve_pos_index: enumVal(
        g(it, "Class_vAction+<AServePosIndex>k__BackingField")
      ),
      b_serve_pos_index: enumVal(
        g(it, "Class_vAction+<BServePosIndex>k__BackingField")
      ),
      action_id: g(it, "Class_bAction+<ActionID>k__BackingField"),
      video_start: g(it, "Class_bAction+<VideoStart>k__BackingField"),
      video_length: g(it, "Class_bAction+<VideoLength>k__BackingField"),
      note: g(it, "Class_bAction+<Note>k__BackingField"),
    });
  }

  return {
    source_file: sourceFile,
    competition: {
      title: g(cup, "<Title>k__BackingField"),
      country_code: g(cup, "<CountryCode>k__BackingField"),
      city: g(cup, "<City>k__BackingField"),
      gender: enumVal(g(cup, "<Gender>k__BackingField")),
      age_category: enumVal(g(cup, "<Age>k__BackingField")),
      start_date: g(cup, "<StartDate>k__BackingField"),
      end_date: g(cup, "<EndDate>k__BackingField"),
      phase: g(phase, "<Name>k__BackingField"),
      group: g(group, "<Name>k__BackingField"),
    },
    match: {
      match_id: g(match, "Class_bMatch+<MatchID>k__BackingField"),
      match_no_or_name: g(match, "Class_bMatch+<Name>k__BackingField"),
      scheduled_datetime: g(
        match,
        "Class_bMatch+<ScheduleDateTime>k__BackingField"
      ),
      video_start_time: g(
        match,
        "Class_bMatch+<VideoStartTime>k__BackingField"
      ),
      time_start: ticksToHms(g(match, "<TimeStart>k__BackingField")),
      time_end: ticksToHms(g(match, "<TimeEnd>k__BackingField")),
      set_count_played: g(match, "<SetCount>k__BackingField"),
      cur_set_num: g(match, "<CurSetNum>k__BackingField"),
      match_state: enumVal(g(match, "<MatchState>k__BackingField")),
      spectators: g(match, "<Spectator>k__BackingField"),
      venue: {
        city: g(place, "<City>k__BackingField"),
        hall: g(place, "<Hall>k__BackingField"),
        pool: g(place, "<Pool>k__BackingField"),
      },
      final_sets_won_a: setsWonA,
      final_sets_won_b: setsWonB,
      note: g(match, "<Note>k__BackingField"),
    },
    referees,
    team_a: buildTeamBlock(atg, ateam),
    team_b: buildTeamBlock(btg, bteam),
    sets: setsOut,
    rally_events: ralliesOut,
  };
}

// ---------------------------------------------------------------------------
// Public entry point: parse a .vis ArrayBuffer into the resolved object graph.
// ---------------------------------------------------------------------------
export function parseVIS(buffer) {
  return new NRBFParser(buffer).load();
}

// Convenience: parse a .vis ArrayBuffer straight to the flattened JSON object.
export function loadVISAsJson(buffer, sourceFile) {
  return visToJson(parseVIS(buffer), sourceFile);
}
